<template>
  <v-dialog v-model="value" max-width="500px" persistent>
    <v-form @submit.prevent="onSubmit" ref="form" v-model="valid">
      <v-card>
        <v-card-title>
          <span data-cy="modal-title" class="headline font-weight-thin">{{ displayName }}</span>
        </v-card-title>
        <v-card-subtitle data-cy="entra-id-subtitle"> Entra ID: {{ user.username }} </v-card-subtitle>

        <v-card-text>
          <v-container>
            <v-row no-gutters>
              <v-col sm="12">
                <v-checkbox data-cy="active-checkbox" label="Active" v-model="user.active"></v-checkbox>
              </v-col>
              <v-col sm="12">
                <v-select
                  data-cy="role-option-list"
                  :items="roleOptionList"
                  item-text="label"
                  item-value="name"
                  label="Role"
                  v-model="user.role"
                ></v-select>
              </v-col>
              <v-col>
                <v-text-field-simplemask
                  data-cy="phone-number"
                  inputMask="SimpleMask"
                  :properties="{
                    clearable: true,
                    placeholder: '',
                    rules: [value => !value || value.length === 13 || 'Phone number must be 10 digits.'], // set to 13 to account for the masking,
                  }"
                  :options="{
                    inputMask: '(###)###-####',
                    outputMask: '##########',
                    empty: null,
                    applyAfter: false,
                    alphanumeric: true,
                  }"
                  v-model="user.phone"
                  label="Phone #"
                ></v-text-field-simplemask>
                <v-text-field :rules="emailRules" data-cy="email" label="Email" v-model="user.email"></v-text-field>
                <v-text-field
                  :rules="firstNameRules"
                  data-cy="firstName"
                  label="First Name"
                  v-model="user.firstName"
                ></v-text-field>
                <v-text-field
                  :rules="lastNameRules"
                  data-cy="lastName"
                  label="Last Name"
                  v-model="user.lastName"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn data-cy="cancel-button" @click="close" outlined>Cancel</v-btn>
          <v-btn data-cy="save-button" @click="save" :disabled="!valid" color="primary">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  async created() {
    await this.fetchRoleList();
  },
  computed: mapState({
    user: state => state.admin.user.edit,
    roleOptionList: state => state.admin.role.list,
    serviceAreaId: state => state.session.serviceArea,
  }),
  data() {
    return {
      displayName: "",
      emailRules: [
        v => !!v || "Email is required",
        v =>
          !v ||
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v
          ) ||
          "Email must be valid",
      ],
      firstNameRules: [v => !!v || "First name is required"],
      lastNameRules: [v => !!v || "Last name is required"],
      valid: false,
    };
  },
  methods: {
    close() {
      this.setUser({});
      this.$emit("input", false);
    },
    async save() {
      // PRIMARY OPERATION - must succeed to close modal
      try {
        await this.updateUser(this.user);
        this.$notifySuccess({ message: "User updated successfully" });
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: `Error updating user ${this.user.username}`, timeout: 0 });
        return; // Early return keeps modal open
      }

      // Modal closes immediately after primary success
      this.close();

      // SECONDARY OPERATIONS - fail gracefully
      try {
        await this.fetchUserList([
          {
            comparator: "=",
            name: "serviceAreaId",
            value: this.serviceAreaId,
          },
        ]);
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Error refreshing list of users" });
      }

      try {
        await this.loadUserListForUserPickers();
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Error refreshing user picker data" });
      }
    },
    ...mapActions({
      fetchRoleList: "admin/role/fetchList",
      fetchUserList: "admin/user/fetchList",
      updateUser: "admin/user/update",
      loadUserListForUserPickers: "user/load",
    }),
    ...mapMutations({
      setUser: "admin/user/setEdit",
    }),
  },
  props: ["value"],
  watch: {
    value(val) {
      if (val) {
        // Capture initial display name when dialog opens
        this.displayName = `${this.user.firstName} ${this.user.lastName}`;
      }
    },
  },
};
</script>
